import { useState } from "react";
import { Check, CreditCard, Sparkles, ArrowRight, Loader2, Star } from "lucide-react";
import { PLANS, PLAN, CREDIT_PACKS, startProCheckout, startStripeCheckout } from "@/lib/subscription";

// Landing-page pricing. Free tier + three credit packs. The legacy
// monthly $19.99 subscription is intentionally not shown here — it lives
// on server-side only for pre-existing subscribers.
export default function PricingSection() {
  const free = PLANS[PLAN.FREE];

  // "" | "packid:whop" | "packid:stripe" — lets each button spin independently.
  const [loadingId, setLoadingId] = useState("");
  const [status, setStatus] = useState("idle"); // idle | unavailable | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleUpgrade = async (packId) => {
    setLoadingId(`${packId}:whop`);
    setStatus("idle");
    setErrorMsg("");
    try {
      const res = await startProCheckout({ plan: packId });
      if (res?.ok && res.purchaseUrl) {
        window.open(res.purchaseUrl, "_blank", "noopener,noreferrer");
        return;
      }
      if (res?.reason === "PLAN_NOT_CONFIGURED" || res?.reason === "NOT_CONFIGURED") {
        setStatus("unavailable");
        return;
      }
      setStatus("error");
      setErrorMsg("Could not open checkout. Please try again.");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again in a moment.");
    } finally {
      setLoadingId("");
    }
  };

  const handleStripeUpgrade = async (packId) => {
    setLoadingId(`${packId}:stripe`);
    setStatus("idle");
    setErrorMsg("");
    try {
      const res = await startStripeCheckout({ plan: packId });
      if (res?.ok && res.url) {
        // Full-page redirect — Stripe redirects back to /payment/success on completion.
        window.location.href = res.url;
        return;
      }
      if (res?.reason === "NOT_CONFIGURED" || res?.reason === "STRIPE_NOT_CONFIGURED") {
        setStatus("unavailable");
        return;
      }
      setStatus("error");
      setErrorMsg("Could not open card checkout. Please try again.");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again in a moment.");
    } finally {
      setLoadingId("");
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 bg-[#fdfbf7] border-y border-[#eae4d9]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
            Pricing
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-[#0f172a] tracking-tight">
            Ready to explore more grooming styles?
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#475569]">
            Unlock more previews and see which look suits your dog best.
          </p>
        </div>

        {/* FREE card */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div
            data-testid="pricing-card-free"
            className="rounded-3xl bg-white border border-[#eae4d9] p-6 sm:p-8 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold tracking-widest uppercase text-[#64748b]">
                {free.label}
              </span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-[#0f172a] bg-[#f3efe6] px-2.5 py-1 rounded-full">
                Available now
              </span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span
                data-testid="pricing-free-price"
                className="font-display text-4xl font-extrabold text-[#0f172a]"
              >
                {free.price}
              </span>
              <span className="text-sm text-[#64748b]">/ {free.period}</span>
            </div>
            <ul className="mt-6 space-y-2.5">
              {free.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-[#1e293b]">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#f3efe6] text-[#0f172a] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="pricing-card-info"
            className="rounded-3xl bg-white border border-dashed border-[#eae4d9] p-6 sm:p-8 flex flex-col justify-center"
          >
            <div className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
              Need more previews?
            </div>
            <p className="mt-2 text-sm text-[#1e293b]">
              Every new user gets 2 free previews. After that, top up with one of the credit packs below — no subscription, only what you need.
            </p>
          </div>
        </div>

        {/* Three credit packs */}
        <div className="mt-10">
          <div className="text-center mb-6">
            <span className="text-xs font-bold tracking-widest uppercase text-[#0f172a]">
              Credit packs
            </span>
          </div>
          <div
            data-testid="pricing-packs"
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
          >
            {CREDIT_PACKS.map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                loadingId={loadingId}
                disabled={!!loadingId && !loadingId.startsWith(pack.id + ":")}
                onSelectWhop={() => handleUpgrade(pack.id)}
                onSelectStripe={() => handleStripeUpgrade(pack.id)}
              />
            ))}
          </div>
        </div>

        {status === "unavailable" && (
          <p
            data-testid="pricing-upgrade-unavailable"
            className="mt-6 text-center text-xs text-[#64748b]"
          >
            Checkout is being finalised — this pack will be available shortly.
          </p>
        )}
        {status === "error" && errorMsg && (
          <p
            data-testid="pricing-upgrade-error"
            className="mt-6 text-center text-xs text-[#c84125]"
          >
            {errorMsg}
          </p>
        )}

        <p className="mt-6 text-xs text-[#64748b] text-center">
          Prices in USD. Credits are added instantly after payment is verified. Preview results are AI-generated and may vary from the final groom.
        </p>
      </div>
    </section>
  );
}

function PackCard({ pack, loadingId, disabled, onSelectWhop, onSelectStripe }) {
  const isPopular = pack.highlight === "Most Popular";
  const isBestValue = pack.highlight === "Best Value";
  const whopBusy = loadingId === `${pack.id}:whop`;
  const stripeBusy = loadingId === `${pack.id}:stripe`;
  return (
    <div
      data-testid={`pricing-pack-${pack.id}`}
      className={
        "relative rounded-3xl p-6 sm:p-7 shadow-sm overflow-hidden transition-all " +
        (isPopular
          ? "bg-[#0f172a] text-white border-2 border-[#ff6b4a] shadow-[0_10px_35px_-8px_rgba(255,107,74,0.35)]"
          : isBestValue
          ? "bg-white text-[#0f172a] border-2 border-[#0f172a]/15 ring-1 ring-[#0f172a]/5 shadow-[0_10px_28px_-12px_rgba(15,23,42,0.25)]"
          : "bg-white text-[#0f172a] border border-[#eae4d9]")
      }
    >
      {isPopular && (
        <>
          <span
            data-testid="pricing-pack-badge-popular"
            className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#ff6b4a] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 shadow"
          >
            <Star className="w-3 h-3" fill="currentColor" /> Most Popular
          </span>
          <div className="pointer-events-none absolute -right-20 -top-20 w-56 h-56 rounded-full bg-[#ff6b4a]/25 blur-3xl" />
        </>
      )}
      {isBestValue && (
        <span
          data-testid="pricing-pack-badge-best-value"
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-[#0f172a] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 shadow"
        >
          Best Value
        </span>
      )}
      <div className="relative">
        <div
          className={
            "text-xs font-bold tracking-widest uppercase " +
            (isPopular ? "text-[#ff9d84]" : "text-[#c84125]")
          }
        >
          {pack.label}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span
            data-testid={`pricing-pack-${pack.id}-price`}
            className="font-display text-4xl font-extrabold"
          >
            {pack.price}
          </span>
          <span
            className={
              "text-sm font-semibold " + (isPopular ? "text-[#cbd5e1]" : "text-[#64748b]")
            }
          >
            {pack.currency}
          </span>
        </div>
        <p
          data-testid={`pricing-pack-${pack.id}-description`}
          className={
            "mt-3 text-sm leading-snug " + (isPopular ? "text-white/90" : "text-[#475569]")
          }
        >
          {pack.description}
        </p>
        {pack.savings && (
          <p
            data-testid={`pricing-pack-${pack.id}-savings`}
            className={
              "mt-2 text-[11px] font-semibold " +
              (isPopular ? "text-[#ff9d84]" : "text-[#c84125]")
            }
          >
            {pack.savings}
          </p>
        )}
        <button
          type="button"
          data-testid={`pricing-pack-${pack.id}-btn`}
          onClick={onSelectWhop}
          disabled={whopBusy || disabled}
          className={
            "mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold tracking-wide uppercase px-5 py-3 transition-all duration-200 active:scale-[0.98] disabled:opacity-70 " +
            (isPopular
              ? "bg-[#ff6b4a] hover:bg-[#e85535] text-white shadow-[0_8px_25px_-4px_rgba(255,107,74,0.35)] hover:shadow-[0_12px_30px_-4px_rgba(255,107,74,0.45)]"
              : "bg-[#0f172a] hover:bg-[#1e293b] text-white")
          }
        >
          {whopBusy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Opening…
            </>
          ) : (
            <>
              {pack.ctaLabel} <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        <button
          type="button"
          data-testid={`pricing-pack-${pack.id}-stripe-btn`}
          onClick={onSelectStripe}
          disabled={stripeBusy || disabled}
          className={
            "mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-5 py-2.5 transition-all duration-200 active:scale-[0.98] disabled:opacity-70 border " +
            (isPopular
              ? "bg-white/5 hover:bg-white/10 text-white border-white/20 hover:border-white/40"
              : "bg-white hover:bg-[#f8fafc] text-[#0f172a] border-[#0f172a]/15 hover:border-[#2563eb]")
          }
        >
          {stripeBusy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" /> Pay with card
            </>
          )}
        </button>
        <div
          className={
            "mt-4 flex items-center justify-center gap-1.5 text-[11px] " +
            (isPopular ? "text-[#ff9d84]" : "text-[#64748b]")
          }
        >
          <Sparkles className="w-3 h-3" /> {pack.credits} AI previews
        </div>
      </div>
    </div>
  );
}
